import { useEffect, useRef, useState } from "react";

const STORAGE_KEY = "hiviewerWelcomeMotionSoundPlayedV2";

type AudioContextConstructor = typeof AudioContext;

const getAudioContextConstructor = (): AudioContextConstructor | undefined => {
  return window.AudioContext || (window as typeof window & { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext;
};

const triggerTone = (
  audioContext: AudioContext,
  destination: AudioNode,
  frequency: number,
  startTime: number,
  duration: number,
  volume: number,
  type: OscillatorType = "sine",
) => {
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, startTime);
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(volume, startTime + 0.08);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

  oscillator.connect(gain);
  gain.connect(destination);
  oscillator.start(startTime);
  oscillator.stop(startTime + duration + 0.05);
};

const playWelcomeSound = async () => {
  const AudioContextClass = getAudioContextConstructor();

  if (!AudioContextClass) return false;

  const audioContext = new AudioContextClass();
  await audioContext.resume();

  if (audioContext.state === "suspended") {
    await audioContext.close();
    return false;
  }

  const now = audioContext.currentTime;
  const masterGain = audioContext.createGain();
  const compressor = audioContext.createDynamicsCompressor();
  const lowShelf = audioContext.createBiquadFilter();
  const highShelf = audioContext.createBiquadFilter();

  masterGain.gain.setValueAtTime(0.65, now);
  compressor.threshold.setValueAtTime(-24, now);
  compressor.ratio.setValueAtTime(3, now);
  compressor.attack.setValueAtTime(0.01, now);
  compressor.release.setValueAtTime(0.2, now);

  lowShelf.type = "lowshelf";
  lowShelf.frequency.setValueAtTime(200, now);
  lowShelf.gain.setValueAtTime(2, now);

  highShelf.type = "highshelf";
  highShelf.frequency.setValueAtTime(3000, now);
  highShelf.gain.setValueAtTime(-6, now);

  masterGain.connect(lowShelf);
  lowShelf.connect(highShelf);
  highShelf.connect(compressor);
  compressor.connect(audioContext.destination);

  triggerTone(audioContext, masterGain, 55, now, 1.8, 0.36);
  triggerTone(audioContext, masterGain, 110, now + 0.04, 1.6, 0.1);
  triggerTone(audioContext, masterGain, 138.59, now + 0.08, 1.4, 0.08);
  triggerTone(audioContext, masterGain, 164.81, now + 0.08, 1.4, 0.08);
  triggerTone(audioContext, masterGain, 220, now + 0.08, 1.4, 0.08);
  triggerTone(audioContext, masterGain, 440, now + 0.12, 1.2, 0.055, "triangle");

  window.setTimeout(() => {
    masterGain.disconnect();
    lowShelf.disconnect();
    highShelf.disconnect();
    compressor.disconnect();
    audioContext.close();
  }, 4000);

  return true;
};

const WelcomeMotionSound = () => {
  const [captureGesture, setCaptureGesture] = useState(false);
  const initRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(STORAGE_KEY)) return;

    let started = false;
    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    const init = () => {
      if (started) return;
      started = true;

      playWelcomeSound()
        .then((played) => {
          if (!played) {
            started = false;
            return;
          }

          window.sessionStorage.setItem(STORAGE_KEY, "true");
          setCaptureGesture(false);
          removeListeners();
        })
        .catch(() => {
          started = false;
        });
    };
    initRef.current = init;

    const removeListeners = () => {
      document.removeEventListener("mousemove", init, true);
      document.removeEventListener("pointermove", init, true);
      document.removeEventListener("mousedown", init, true);
      document.removeEventListener("touchstart", init, true);
      document.removeEventListener("pointerdown", init, true);
      document.removeEventListener("click", init, true);
      document.removeEventListener("keydown", init, true);
    };

    setCaptureGesture(true);

    if (isTouchDevice) {
      document.addEventListener("touchstart", init, { passive: true, capture: true });
      document.addEventListener("pointerdown", init, { passive: true, capture: true });
    } else {
      document.addEventListener("mousemove", init, { passive: true, capture: true });
      document.addEventListener("pointermove", init, { passive: true, capture: true });
    }
    document.addEventListener("mousedown", init, { passive: true, capture: true });
    document.addEventListener("click", init, { passive: true, capture: true });
    document.addEventListener("keydown", init, { capture: true });

    return () => {
      initRef.current = () => undefined;
      removeListeners();
    };
  }, []);

  if (!captureGesture) return null;

  return (
    <div
      aria-hidden="true"
      onClick={() => initRef.current()}
      onMouseMove={() => initRef.current()}
      onPointerDown={() => initRef.current()}
      onPointerMove={() => initRef.current()}
      onTouchStart={() => initRef.current()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483647,
        cursor: "default",
        background: "transparent",
      }}
    />
  );
};

export default WelcomeMotionSound;
